<template>
  <div
    :class="{
      'content-summary-snippet': true,
      'content-summary-snippet--hide-decorations': props.hideDecorations,
    }"
    style="position: relative"
  >
    <div class="content-summary-snippet__delete-button-wrap">
      <CircleWithIconBtn
        v-if="props.isUnbindButtonVisible"
        class="content-summary-snippet__delete-button"
        iconName="scissors_3"
        iconColor="rgba(180, 80, 80, 1)"
        iconColorHover="rgba(180, 80, 80, 1)"
        color="white"
        :iconSize="16"
        size="big"
        @click="emit('clickDelete')"
      />
    </div>

    <div v-if="props.isRemoveButtonVisible" style="position: absolute; top: 7px; right: 5px; z-index: 10">
      <LinkWithIcon isNoUnderline @click="emit('clickRemove')">
        <template #icon>
          <IconEmbedded name="remove_2" :size="17" />
        </template>
      </LinkWithIcon>
    </div>

    <div v-if="props.isContentSourcedFromLabelVisible" class="content-summary-snippet__title">
      Content Sourced From
      <!--<Matched to {{ lodashStartCase(props.contentSummaryData.contextType) }}-->
    </div>

    <!-- Creator visit summary -->
    <div class="creator-visit-summary content-summary-snippet__content">
      <div class="creator-visit-summary__title-wrap">
        <span class="global-h4 creator-visit-summary__title">{{ props.contentSummaryData.contextName }}</span>
        <LinkWithIcon tag="RouterLink" :to="contextSourceLink" target="_blank" isNoUnderline color="light-gray" style="margin-left: 7px; position: relative; top: 2px">
          <template #icon><IconEmbedded name="external-link_2" :size="17" /></template>
        </LinkWithIcon>

        <InfoIconWithTooltip v-if="props.matchRationale && !props.matchRationale.startsWith('LocationExternalId')" style="position: relative; top: 2px; margin-left: 4px">
          <b>Why this content?</b><br />{{ props.matchRationale }}
        </InfoIconWithTooltip>
      </div>

      <!--<div class="creator-visit-summary__thumbnails-row">-->
      <!--  <div class="creator-visit-summary__thumbnails-row-in-wrap">-->
      <!--    <SrpFileThumbnail-->
      <!--      class="creator-visit-summary__thumbnail"-->
      <!--      v-for="fileName in props.contentSummaryData.examples"-->
      <!--      :key="fileName"-->
      <!--      :fileName="fileName"-->
      <!--      :isFixedAspectRatio="false"-->
      <!--      :isWithLoader="false"-->
      <!--    />-->
      <!--  </div>-->
      <!--</div>-->

      <div class="creator-visit-summary__assets-row">
        <LinkWithIcon class="creator-visit-summary__assets-count-link" isDottedUnderline @click="emit('clickViewPhotos')">
          <!--<template #icon><IconEmbedded name="image_2" :size="17" /></template>-->
          <span>{{ props.contentSummaryData.assetCount }} Photo{{ props.contentSummaryData.assetCount > 1 ? "s" : "" }}</span>
        </LinkWithIcon>
        <div v-if="formattedContentDate" class="creator-visit-summary__date-wrap">
          <span class="creator-visit-summary__date-separator" />
          <span class="creator-visit-summary__content-date">{{ formattedContentDate }}</span>
        </div>
      </div>
    </div>
    <!-- / Creator visit summary -->
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";

// Types
import { ContentSummaryPreview } from "@contracts/contentCalendar";

// Components
import CircleWithIconBtn from "@components/ui/CircleWithIconBtn.vue";
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import InfoIconWithTooltip from "@components/ui/InfoIconWithTooltip.vue";
import LinkWithIcon from "@components/LinkWithIcon.vue";

const props = withDefaults(
  defineProps<{
    contentSummaryData: ContentSummaryPreview;
    matchRationale?: string | null;
    isUnbindButtonVisible: boolean;
    customerId: string;
    isContentSourcedFromLabelVisible?: boolean;
    hideDecorations?: boolean;
    isRemoveButtonVisible?: boolean;
  }>(),
  {
    matchRationale: null,
    isContentSourcedFromLabelVisible: true,
    hideDecorations: false,
    isRemoveButtonVisible: false,
  }
);

const emit = defineEmits<{
  (e: "clickDelete"): void;
  (e: "clickViewPhotos"): void;
  (e: "clickRemove"): void;
}>();

// Generates the Context Source Link ==========================================
const contextSourceLink = computed<object>(() => {
  return {
    CreatorVisit: {
      name: "PaidCollabCommunityAdmin",
      params: { communityId: props.customerId },
      hash: `#collab=${props.contentSummaryData.contextId}`,
    },
    CommunityUpload: {
      name: "CommunityPhotos",
      params: { pageId: props.customerId },
      hash: `#group=community-upload&sectionId=${props.contentSummaryData.contextId}`,
    },
    CustomerUpload: {
      name: "CommunityPhotos",
      params: { pageId: props.customerId },
      hash: `#group=my-uploads&sectionId=${props.contentSummaryData.contextId}`,
    },
    Location: {
      name: "CommunityPhotos",
      params: { pageId: props.customerId },
      hash: `#group=location&sectionId=${props.contentSummaryData.contextId}`,
    },
  }[props.contentSummaryData.contextType];
});

// Formatted content date (uses contentDate if present, falls back to lastProcessed)
const formattedContentDate = computed<string | null>(() => {
  const dateStr = props.contentSummaryData.contentDate ?? props.contentSummaryData.lastProcessed;
  if (!dateStr) return null;
  const date = new Date(dateStr);
  return date.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
});
</script>

<style scoped lang="scss">
@import "./contentSummarySnippet.scss";
@import "@/scss/variables.scss";

// Creator visit summary ======================================================
.creator-visit-summary {
  &__title-wrap {
    margin-bottom: 9px;
  }

  &__title {
    font-weight: 500;
  }

  &__thumbnails-row {
    width: calc(100% + 10px);
    height: 35px;
    margin-bottom: 9px;
    position: relative;
    z-index: 0;
    overflow: hidden;
    mask-image: linear-gradient(90deg, rgba(0, 0, 0, 1) 80%, rgba(0, 0, 0, 0) 95%);
  }

  &__thumbnails-row-in-wrap {
    width: 1000px;
    height: 35px;
    gap: 4px;
    display: flex;
    position: absolute;
    inset: 0 auto auto 0;
  }

  &__thumbnail {
    height: 35px;
    border-radius: 3px;

    :deep(*) {
      border-radius: 3px !important;
    }
  }

  &__assets-row {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    row-gap: 4px;
  }

  &__assets-count-link {
    @include standardFont;
    // margin-left: -4px;
    white-space: nowrap;
  }

  &__date-wrap {
    display: inline-flex;
    align-items: center;
    white-space: nowrap;
  }

  &__date-separator {
    width: 1px;
    height: 14px;
    margin: 0 8px;
    background: rgba(0, 0, 0, 0.2);
    border-radius: 1px;
    flex-shrink: 0;
  }

  &__content-date {
    @include standardFont;
    white-space: nowrap;
  }
}
</style>
